// Header.jsx - Converted to Tailwind CSS with Breadcrumb Navigation
import { useState, useContext, useEffect, useRef } from "react";
import { AuthContext } from "../context/AuthContext.jsx";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";

import {
  getAllNotificationsApi,
  readNotificationApi,
} from "../api/global-api/api-notification.js";
import getSocket from "../services/socket.js";
import { formatTrades } from "utils/trade-format";
import { useBreadcrumbLabelMap } from "utils/breadcrumb-labels";
import {
  Bell,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Menu,
  Settings,
  Calendar,
  AlertCircle,
  MessageSquare,
  CheckCircle,
  LogIn,
  LogOut,
  FileText,
  Lock,
  X,
  Home,
  Clock,
  Sun,
  Moon,
  BadgeCheck,
  Briefcase,
  Phone,
  ShieldCheck,
} from "lucide-react";
import LogoutDialog from "./LogoutDialog.jsx";
import {
  resolveDatePrefs,
  getGreeting,
  formatDateByPref,
  formatTimeByPref,
  getTimeZoneLabel,
  getRelativeTime,
} from "../services/date-converter.js";
import Avatar from "components/ui/Avatar.jsx";
import Skeleton from "components/ui/Skeleton.jsx";
import { useTheme } from "../context/ThemeContext.jsx";
import { useSettings } from "../context/SettingsContext.jsx";
// console.log("Header.jsx loaded", "AuthContext:", AuthContext, "ThemeContext:", useTheme, "SettingsContext:",useSettings);
import apiUserSettings from "../api/global-api/api-settings.js";
import { buildSettingsFormState } from "../modules/swiftboard/settings/settings-shared.js";

export default function Header({ toggleSidebar, isSidebarOpen, isMobile, mobileOpen }) {
  const socket = getSocket();
  const { user } = useContext(AuthContext);
  const { isDark, toggleTheme } = useTheme();
  // Settings (incl. theme) are fetched once during app bootstrap by
  // SettingsContext — ProtectedRoute already waits for it, so by the time
  // Header mounts, settingsRecord/settingsId are ready.
  const { settings: settingsRecord, settingsId, updateSettingsLocal } = useSettings();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [notifications, setNotifications] = useState([]);
  // Derived so it can never drift from the list (re-pushed / cleared chat notifications)
  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Header waits for notifications to resolve before rendering, so the
  // notification badge never flashes an empty state on first paint.
  const [initialLoading, setInitialLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();
  // Page-registered overrides ("" hides a segment), see utils/breadcrumb-labels.js
  const breadcrumbLabels = useBreadcrumbLabelMap();

  // Notification sound: persists across renders. Browser autoplay policy blocks
  // sound until the user interacts with the page, so we track an "unlocked" flag.
  const audioRef = useRef(null);
  const audioUnlockedRef = useRef(false);

  // Live clock — ticks every second for the header greeting pill
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Get current path segments
  const getPathSegments = () => {
    const pathname = location.pathname;
    // Remove /swiftboard from the path
    const cleanPath = pathname.replace(/^\/swiftboard/, "");
    const segments = cleanPath.split("/").filter((segment) => segment);
    return segments;
  };

  const getNotifVariant = (priority) => {
    const map = {
      URGENT: {
        bg: "var(--color-danger-bg)",
        fg: "var(--color-danger)",
        border: "var(--color-danger-border)",
        accent: "#A32D2D",
      },
      HIGH: {
        bg: "var(--color-high-bg)",
        fg: "var(--color-high)",
        border: "var(--color-high-border)",
        accent: "#D85A30",
      },
      SUCCESS: {
        bg: "var(--color-success-bg)",
        fg: "var(--color-success)",
        border: "var(--color-success-border)",
        accent: "#3B6D11",
      },
      WARNING: {
        bg: "var(--color-warning-bg)",
        fg: "var(--color-warning)",
        border: "var(--color-warning-border)",
        accent: "#BA7517",
      },
      INFO: {
        bg: "var(--color-primary-tint)",
        fg: "var(--color-primary)",
        border: "var(--color-border-strong)",
        accent: "#534AB7",
      },
    };
    return map[priority] || map.INFO;
  };

  // Format breadcrumb text (capitalize and replace hyphens)
  const formatBreadcrumbText = (text) => {
    if (breadcrumbLabels[text]) return breadcrumbLabels[text];
    return text
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  // Generate breadcrumb navigation
  const renderBreadcrumbs = () => {
    const segments = getPathSegments();

    return (
      <nav
        className="flex items-center gap-1 flex-nowrap overflow-x-auto scrollbar-hidden min-w-0"
        aria-label="Breadcrumb"
      >
        {/* Home/SwiftBoard root */}
        <div className="flex items-center flex-shrink-0">
          <button
            onClick={() => navigate("/swiftboard")}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-text_secondary hover:text-secondary rounded-lg transition-colors duration-200 hover:bg-surface_alt flex-shrink-0"
          >
            <Home size={15} />
            <span className="hidden sm:inline">SwiftBoard</span>
          </button>
          {segments.length > 0 && (
            <ChevronRight size={14} className="text-text_muted mx-0.5 flex-shrink-0" />
          )}
        </div>

        {/* Dynamic breadcrumb segments */}
        {segments.map((segment, index) => {
          if (breadcrumbLabels[segment] === "") return null;
          const isLast = index === segments.length - 1;
          const path = `/swiftboard/${segments.slice(0, index + 1).join("/")}`;

          return (
            <div key={segment} className="flex items-center flex-shrink-0 min-w-0">
              {isLast ? (
                // Current page (non-clickable)
                <div className="flex items-center px-2.5 py-1.5 min-w-0">
                  <span className="text-sm font-semibold bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent truncate max-w-[140px] md:max-w-[240px]">
                    {formatBreadcrumbText(segment)}
                  </span>
                </div>
              ) : (
                // Clickable parent pages
                <>
                  <button
                    onClick={() => navigate(path)}
                    className="flex items-center px-2.5 py-1.5 text-sm font-medium text-text_secondary hover:text-secondary rounded-lg transition-colors duration-200 hover:bg-surface_alt min-w-0"
                  >
                    <span className="truncate max-w-[90px] md:max-w-[160px]">
                      {formatBreadcrumbText(segment)}
                    </span>
                  </button>
                  <ChevronRight size={14} className="text-text_muted mx-0.5 flex-shrink-0" />
                </>
              )}
            </div>
          );
        })}
      </nav>
    );
  };

  useEffect(() => {
    // Served from S3 (cross-origin to the Amplify app), so the SPA rewrite that
    // was turning the local /sounds/notification.mp3 request into index.html
    // never applies here. This plays correctly in production.
    const audio = new Audio(
      "https://swiftboard-bucket.s3.ap-south-1.amazonaws.com/sounds/universfield-level-up-191997.mp3"
    );
    audio.preload = "auto";
    audioRef.current = audio;

    // Surface any problem loading the file itself (404, wrong MIME type, CDN
    // rewrite serving index.html). Without this the failure is invisible.
    audio.addEventListener("error", () => {
      console.log(
        "Notification audio failed to load:",
        audio.error,
        audio.currentSrc,
      );
    });

    // Browser autoplay policy blocks audio that isn't tied to a user gesture.
    // On each user interaction we try to "unlock" the element by playing it
    // (muted) once. IMPORTANT: only stop listening once unlock SUCCEEDS — if the
    // first attempt rejects (slow load, transient block) we must keep listening
    // so a later interaction can succeed.
    const unlockAudio = () => {
      if (audioUnlockedRef.current) return;
      audio.muted = true;
      audio
        .play()
        .then(() => {
          audio.pause();
          audio.currentTime = 0;
          audio.muted = false;
          audioUnlockedRef.current = true;
          window.removeEventListener("pointerdown", unlockAudio);
          window.removeEventListener("keydown", unlockAudio);
        })
        .catch((err) => {
          audio.muted = false;
          // Keep the listeners attached so the next interaction retries.
          console.log("Notification audio unlock failed:", err);
        });
    };
    window.addEventListener("pointerdown", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    // iOS Safari (and some embedded webviews) do NOT expose window.Notification.
    // Touching it unguarded throws a TypeError inside this effect, and with no
    // ErrorBoundary that unmounts the whole app → blank screen after login.
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      try {
        Notification.requestPermission();
      } catch (err) {
        console.log("Notification.requestPermission unsupported:", err);
      }
    }

    // The server can re-push the same chat notification row for every new
    // message in a conversation — replace it by id and move it to the top
    // instead of stacking duplicates.
    socket.on("new-notification", (data) => {
      setNotifications((prev) => [data, ...prev.filter((n) => n.id !== data.id)]);

      // Only attempt to play once the user has interacted (autoplay unlocked).
      // Otherwise the browser rejects play() with NotAllowedError.
      if (audioUnlockedRef.current) {
        audio.currentTime = 0;
        audio.play().catch((err) => console.log("Audio play blocked:", err));
      }

      if (typeof Notification !== "undefined" && Notification.permission === "granted") {
        const notification = new Notification(
          data.title || "New Notification",
          {
            body: data.message || "You have a new notification",
            icon: "/logo.png",
          },
        );

        notification.onclick = () => {
          window.focus();
          if (data?.action_url) navigate(data.action_url);
          notification.close();
        };
      }
    });

    // Chat notifications cleared server-side once their conversation is read
    const handleNotificationsRead = ({ ids = [] } = {}) => {
      setNotifications((prev) =>
        prev.map((n) => (ids.includes(n.id) ? { ...n, is_read: true } : n)),
      );
    };
    socket.on("notifications-read", handleNotificationsRead);

    return () => {
      socket.off("new-notification");
      socket.off("notifications-read", handleNotificationsRead);
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  useEffect(() => {
    const loadNotifications = async () => {
      setLoading(true);
      try {
        const res = await getAllNotificationsApi({ user_fid: user.id, skip: 0, limit: 20 });
        const notifications = res.data;
        setNotifications(notifications);
      } catch (err) {
        console.log("Load Notifications Error:", err);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    };

    loadNotifications();
  }, []);

  // Quick theme toggle: flip the UI instantly (ThemeContext), then persist the
  // choice to the backend and sync SettingsContext so every consumer
  // (Settings page, Header, future features) reflects it immediately.
  const handleThemeToggle = async () => {
    const nextTheme = isDark ? "LIGHT" : "DARK";
    toggleTheme();

    if (!settingsId) return;
    try {
      const payload = { ...buildSettingsFormState(settingsRecord), theme: nextTheme };
      await apiUserSettings.updateSettings(settingsId, payload);
      updateSettingsLocal({ theme: nextTheme });
    } catch (err) {
      console.log("Save theme preference error:", err);
    }
  };

  // User's preferred timezone/date/time format from Settings — shared with
  // every other page via services/date-converter.js, falls back to the
  // browser's local timezone and a sane default until settings load.
  const datePrefs = resolveDatePrefs(settingsRecord);

  const getHeaderDate = () => formatDateByPref(currentTime, datePrefs);
  const getHeaderTime = () => formatTimeByPref(currentTime, datePrefs);
  const headerTzLabel = getTimeZoneLabel(currentTime, datePrefs);
  // Full name on hover, e.g. "(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi"
  const headerTzTitle = settingsRecord?.timezone?.display_name || datePrefs.timeZone;

  const markAsRead = (id) => {
    const notification = notifications.find((n) => n.id === id);
    if (!notification || notification.is_read) {
      return;
    }
    const payload = {
      ids: [id],
    };
    readNotificationApi(payload);
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === id ? { ...notif, is_read: true } : notif,
      ),
    );
  };

  // Mark read + open the linked page (e.g. the conversation chat)
  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    if (notification?.action_url) {
      navigate(notification.action_url);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    const unreadIds = notifications
      .filter((notif) => !notif.is_read)
      .map((notif) => notif.id);
    if (unreadIds.length === 0) return;
    await readNotificationApi({
      ids: unreadIds,
    });
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.is_read ? notif : { ...notif, is_read: true },
      ),
    );
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case "LOGIN":
        return <LogIn size={16} />;
      case "DEADLINE":
        return <Calendar size={16} />;
      case "SYSTEM":
      case "CHAT_MESSAGE":
        return <MessageSquare size={16} />;
      case "status":
        return <CheckCircle size={16} />;
      case "PROJECT":
        return <FileText size={16} />;
      case "task":
        return <CheckCircle size={16} />;
      default:
        return <AlertCircle size={16} />;
    }
  };

  if (initialLoading) {
    return (
      <header className="flex justify-between items-center px-4 md:px-6 h-[64px] bg-surface border-b border-border shadow-sm sticky top-0 z-[100] font-sans">
        <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
          <Skeleton className="w-9 h-9 flex-shrink-0" rounded="rounded-md" />
          <Skeleton className="h-4 w-40 hidden sm:block" />
        </div>
        <div className="flex items-center gap-2 md:gap-3">
          <Skeleton className="w-9 h-9 flex-shrink-0" rounded="rounded-md" />
          <Skeleton className="w-9 h-9 flex-shrink-0" rounded="rounded-md" />
          <div className="flex items-center gap-2.5 pl-2 md:pl-3 border-l border-border">
            <Skeleton className="h-8 w-8 flex-shrink-0" rounded="rounded-full" />
            <Skeleton className="h-4 w-24 hidden sm:block" />
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="flex justify-between items-center px-4 md:px-6 h-[64px] bg-surface border-b border-border shadow-sm sticky top-0 z-[100] font-sans">
      <div className="flex items-center gap-3 md:gap-4 flex-1 min-w-0">
        {/* Sidebar Toggle */}
        <button
          className="flex-shrink-0 bg-surface_alt border border-border text-text_secondary w-9 h-9 rounded-md flex items-center justify-center cursor-pointer hover:border-border_strong hover:text-primary"
          onClick={toggleSidebar}
          aria-label={isMobile ? (mobileOpen ? "Close menu" : "Open menu") : (isSidebarOpen ? "Collapse sidebar" : "Expand sidebar")}
        >
          {isMobile ? (
            mobileOpen ? <X size={18} /> : <Menu size={18} />
          ) : (
            isSidebarOpen ? <ChevronLeft size={18} /> : <ChevronRight size={18} />
          )}
        </button>

        {/* Breadcrumb Navigation - hidden on phones to avoid cramped alignment */}
        <div className="flex-1 min-w-0 hidden sm:block">{renderBreadcrumbs()}</div>
      </div>

      <div className="flex items-center gap-2 md:gap-3">
        {/* Greeting + Live Clock */}
        <div className="hidden lg:flex items-center gap-3 pr-3 border-r border-border">
          <div className="flex flex-col items-end leading-tight">
            <span className="text-sm font-semibold text-text_primary">
              {getGreeting(datePrefs.timeZone)},{" "}
              <span className="bg-gradient-to-r from-secondary to-primary bg-clip-text text-transparent">
                {user?.first_name || "there"}
              </span>
            </span>
            <span className="text-[11px] text-text_muted font-medium">
              {getHeaderDate()}
            </span>
          </div>
          <div
            className="flex items-center gap-1.5 h-9 bg-surface_alt border border-border rounded-md px-2.5"
            title={headerTzTitle}
          >
            <Clock size={14} className="text-secondary" />
            <span className="text-xs font-semibold text-text_primary tabular-nums tracking-tight min-w-[62px]">
              {getHeaderTime()}
            </span>
            <span className="border-l border-border pl-1.5 text-[11px] font-semibold text-text_muted whitespace-nowrap">
              {headerTzLabel}
            </span>
          </div>
        </div>

        {/* Theme Toggle */}
        <button
          className="flex-shrink-0 bg-surface_alt border border-border w-9 h-9 rounded-md flex items-center justify-center text-text_secondary cursor-pointer hover:border-border_strong hover:text-primary"
          onClick={handleThemeToggle}
          aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? <Sun size={17} /> : <Moon size={17} />}
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            className="relative bg-surface_alt border border-border w-9 h-9 rounded-md flex items-center justify-center text-text_secondary cursor-pointer hover:border-border_strong hover:text-primary"
            onClick={() => {
              setIsNotificationOpen(!isNotificationOpen);
              setIsProfileOpen(false);
            }}
            aria-label="Notifications"
          >
            {/* Bell — rings only while there are unread notifications */}
            <motion.div
              animate={
                unreadCount > 0
                  ? { rotate: [0, -15, 15, -10, 10, 0] }
                  : { rotate: 0 }
              }
              transition={{
                duration: 0.6,
                repeat: unreadCount > 0 ? Infinity : 0,
                repeatDelay: 3,
              }}
              style={{ originY: 0 }}
            >
              <Bell size={17} />
            </motion.div>

            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-secondary text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center border-2 border-surface">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {isNotificationOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-[998] bg-transparent"
                onClick={() => setIsNotificationOpen(false)}
              />

              {/* Dropdown — fixed to the viewport on phones so it can't be pushed
                  off-screen by the bell button's position; anchored to the
                  button itself from sm: up */}
              <div
                className="fixed left-4 right-4 top-[70px] sm:absolute sm:left-auto sm:top-[calc(100%+10px)] sm:right-0 w-auto sm:w-[350px] md:w-[380px] max-w-[calc(100vw-2rem)] bg-surface rounded-2xl border border-border overflow-hidden z-[1000] shadow-[0_16px_40px_rgba(0,0,0,0.12)]"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex justify-between items-center px-[18px] py-4 border-b border-border">
                  <div className="flex items-center gap-2.5">
                    <h3 className="m-0 text-sm font-medium text-text_primary">
                      Notifications
                    </h3>
                    {unreadCount > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-primary text-white text-[10.5px] font-medium">
                        {unreadCount}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {unreadCount > 0 && (
                      <button
                        className="bg-none border-none text-info text-xs font-medium cursor-pointer px-2.5 py-1.5 rounded-md hover:bg-info_bg transition-colors"
                        onClick={markAllAsRead}
                      >
                        Mark all read
                      </button>
                    )}
                    <button
                      className="bg-none border-none text-text_muted cursor-pointer p-1.5 rounded-md flex items-center justify-center hover:bg-surface_alt hover:text-text_secondary transition-colors"
                      onClick={() => setIsNotificationOpen(false)}
                      aria-label="Close notifications"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>

                {/* List */}
                <div className="max-h-[min(420px,65vh)] overflow-y-auto">
                  {notifications.length > 0 ? (
                    notifications.map((notification) => {
                      const variant = getNotifVariant(notification.varient);
                      return (
                        <div
                          key={notification.id}
                          className={`relative flex items-start gap-3 px-[18px] py-3.5 border-b border-border cursor-pointer transition-colors hover:bg-surface_alt ${
                            notification.is_read ? "" : "bg-primary/[0.04]"
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          {/* Unread accent bar */}
                          {!notification.is_read && (
                            <span
                              className="absolute left-0 top-0 bottom-0 w-[3px]"
                              style={{ background: variant.accent }}
                            />
                          )}

                          {/* Icon chip */}
                          <div
                            className="flex-shrink-0 w-9 h-9 rounded-[10px] flex items-center justify-center"
                            style={{
                              background: variant.bg,
                              color: variant.fg,
                            }}
                          >
                            {getNotificationIcon(notification.type)}
                          </div>

                          {/* Body */}
                          <div className="flex-1 min-w-0">
                            <h4 className="m-0 mb-0.5 text-[13px] font-medium text-text_primary">
                              {notification.title}
                            </h4>
                            <p className="m-0 mb-[7px] text-xs text-text_secondary leading-relaxed">
                              {notification.message}
                            </p>
                            <div className="flex items-center gap-2">
                              <span className="text-[11px] text-text_muted">
                                {getRelativeTime(notification.created_date)}
                              </span>
                              <span
                                className="text-[9.5px] px-[7px] py-0.5 rounded-full font-medium tracking-[0.03em] border"
                                style={{
                                  background: variant.bg,
                                  color: variant.fg,
                                  borderColor: variant.border,
                                }}
                              >
                                {notification.priority || "INFO"}
                              </span>
                            </div>
                          </div>

                          {/* Unread dot */}
                          {!notification.is_read && (
                            <span className="absolute top-4 right-4 w-[7px] h-[7px] bg-secondary rounded-full" />
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="flex flex-col items-center py-12 px-5 text-text_muted text-center">
                      <div className="w-12 h-12 rounded-full bg-surface_alt flex items-center justify-center mb-3">
                        <Bell size={22} className="opacity-60" />
                      </div>
                      <p className="m-0 mb-1 text-sm font-medium text-text_secondary">
                        No notifications
                      </p>
                      <span className="text-xs">You're all caught up</span>
                    </div>
                  )}
                </div>

                {/* Footer */}
                {notifications.length > 0 && (
                  <div className="px-[18px] py-2.5 border-t border-border text-center">
                    <button className="bg-none border-none text-primary text-[12.5px] font-medium cursor-pointer hover:underline">
                      View all notifications
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* User Profile */}
        {/* No box here: avatar + name sit on the header itself, set off by a
            divider, and the 36px row (h-9) lines up with the icon buttons. */}
        <div className="relative flex items-center pl-2 md:pl-3 border-l border-border">
          <button
            className="flex items-center gap-2.5 h-9 text-left cursor-pointer"
            onClick={() => {
              setIsProfileOpen(!isProfileOpen);
              setIsNotificationOpen(false);
            }}
            aria-label="User profile"
          >
            {/* Avatar with online dot. `flex` stops Avatar's inline-block root
                from sitting on a text line, which added space under the photo
                and pushed the dot below it. */}
            <div className="flex flex-shrink-0">
              <Avatar user={user} size={32} rounded={true} status="ONLINE" />
            </div>

            <div className="hidden sm:flex flex-col min-w-0 max-w-[160px]">
              <span className="truncate text-sm leading-5 font-semibold text-text_primary">
                {user?.first_name} {user?.last_name?.charAt(0) || ""}
              </span>
              <span className="truncate text-xs leading-4 text-text_muted">
                {user?.company?.company_name || "Surya Group"}
              </span>
            </div>

            <ChevronDown
              size={16}
              className={`text-text_muted hidden sm:block transition-transform duration-200 ${
                isProfileOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Profile Dropdown */}
          {isProfileOpen && (
            <>
              {/* Backdrop for profile */}
              <div
                className="fixed inset-0 z-[998] bg-transparent"
                onClick={() => setIsProfileOpen(false)}
              />

              <div className="absolute top-[calc(100%+10px)] right-0 w-[calc(100vw-2rem)] sm:w-[340px] md:w-[380px] max-w-[calc(100vw-2rem)] bg-surface rounded-2xl shadow-xl border border-border overflow-hidden z-[1000]">
                <div className="p-5 md:p-6 bg-gradient-to-br from-primary to-primary_hover text-white">
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/10 flex items-center justify-center mb-3 md:mb-4 flex-shrink-0">
                    <Avatar user={user} size={56} rounded={false} />
                  </div>
                  <div className="min-w-0">
                    <h4 className="m-0 mb-1 text-base md:text-lg font-semibold">
                      {user?.first_name} {user?.last_name || ""}
                    </h4>
                    <p className="m-0 mb-2 md:mb-3 opacity-80 text-xs break-words">
                      {user?.email}
                    </p>
                    <div className="flex gap-2 flex-wrap items-center">
                      {user?.user_role?.role && (
                        <span className="inline-flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-lg text-[11px] md:text-xs font-bold tracking-wide bg-white text-primary shadow-sm">
                          <ShieldCheck size={13} />
                          {user.user_role.role}
                        </span>
                      )}
                      {user?.company && (
                        <span className="px-2 py-1 rounded-lg text-[10px] md:text-[11px] font-semibold tracking-wide bg-white/10 text-white">
                          {user.company.company_name}
                        </span>
                      )}
                      {user?.mail_verified_date && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] md:text-[11px] font-semibold tracking-wide bg-white/10 text-white">
                          <BadgeCheck size={12} />
                          Verified
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="h-px bg-border mx-4 md:mx-6"></div>

                <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-4 md:px-6 py-4">
                  {user?.designation?.designation_name && (
                    <div className="flex items-start gap-2 min-w-0">
                      <Briefcase size={14} className="text-text_muted mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[10px] md:text-[11px] text-text_muted uppercase tracking-wide mb-0.5">
                          Designation
                        </div>
                        <div className="text-xs md:text-sm text-text_secondary font-medium break-words">
                          {user.designation.designation_name}
                        </div>
                      </div>
                    </div>
                  )}
                  {user?.phone && (
                    <div className="flex items-start gap-2 min-w-0">
                      <Phone size={14} className="text-text_muted mt-0.5 flex-shrink-0" />
                      <div className="min-w-0">
                        <div className="text-[10px] md:text-[11px] text-text_muted uppercase tracking-wide mb-0.5">
                          Phone
                        </div>
                        <div className="text-xs md:text-sm text-text_secondary font-medium break-words">
                          {user.phone}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {user?.assigned_admin && (
                  <>
                    <div className="h-px bg-border mx-4 md:mx-6"></div>
                    <div className="flex items-center gap-3 px-4 md:px-6 py-3">
                      <Avatar user={user.assigned_admin} size={32} rounded />
                      <div className="min-w-0">
                        <div className="text-[10px] md:text-[11px] text-text_muted uppercase tracking-wide mb-0.5">
                          Assigned Admin
                        </div>
                        <div className="text-xs md:text-sm text-text_primary font-medium break-words">
                          {user.assigned_admin.first_name} {user.assigned_admin.last_name}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                <div className="h-px bg-border mx-4 md:mx-6"></div>

                <button
                  onClick={() => navigate("/swiftboard/your-account")}
                  className="flex items-center gap-3 w-full px-4 md:px-6 py-2.5 border-none bg-none text-text_secondary cursor-pointer transition-colors hover:bg-surface_alt text-sm"
                >
                  <User size={16} className="text-text_secondary" />
                  <span>Your Account</span>
                </button>
                <button
                  onClick={() => navigate("/swiftboard/login-security")}
                  className="flex items-center gap-3 w-full px-4 md:px-6 py-2.5 border-none bg-none text-text_secondary cursor-pointer transition-colors hover:bg-surface_alt text-sm"
                >
                  <Lock size={16} className="text-text_secondary" />
                  <span>Login & security</span>
                </button>
                <button
                  onClick={() => {
                    setIsProfileOpen(false);
                    navigate("/swiftboard/settings");
                  }}
                  className="flex items-center gap-3 w-full px-4 md:px-6 py-2.5 border-none bg-none text-text_secondary cursor-pointer transition-colors hover:bg-surface_alt text-sm"
                >
                  <Settings size={16} className="text-text_secondary" />
                  <span>Settings</span>
                </button>

                {user?.trade_of_works?.length > 0 && (
                  <div className="px-4 md:px-6 py-3">
                    <div className="text-[10px] md:text-[11px] text-text_muted uppercase tracking-wide mb-1">
                      Trade of Work
                    </div>
                    <div className="text-xs md:text-sm text-text_secondary font-medium">
                      {formatTrades(user.trade_of_works)}
                    </div>
                  </div>
                )}

                {user?.city && (
                  <div className="px-4 md:px-6 py-3">
                    <div className="text-[10px] md:text-[11px] text-text_muted uppercase tracking-wide mb-1">
                      Location
                    </div>
                    <div className="text-xs md:text-sm text-text_secondary font-medium flex items-center gap-1.5">
                      <span className="text-xs">📍</span>
                      {user.city}, {user?.country_directory?.country_name || ""}
                    </div>
                  </div>
                )}

                <div className="h-px bg-border mx-4 md:mx-6"></div>

                <button
                  onClick={() => {
                    setIsProfileOpen(false);
                    setShowLogoutDialog(true);
                  }}
                  className="flex items-center gap-3 w-full px-4 md:px-6 py-2.5 border-none bg-none text-danger cursor-pointer hover:bg-danger_bg text-sm"
                >
                  <LogOut size={16} className="text-danger" />
                  <span>Log out</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <LogoutDialog
        isOpen={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        centered
      />
    </header>
  );
}
